import React, { useState, useEffect } from "react";
import { LiveProvider, LiveEditor, LiveError, LivePreview } from "react-live";
import { Paragraph, Flex, IconButton, Toast, ScrollArea } from "../src/index";
import { SizeIcon, CopyIcon } from "sebikostudio-icons";

const ReactLive = ({ children, scope, vertical }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [showToast, setShowToast] = useState(false);

  // Step 1: Controlled code state
  const [liveCode, setLiveCode] = useState(children);
  // Step 2: Debounced code for LiveProvider
  const [debouncedCode, setDebouncedCode] = useState(children);

  // Step 3: showErrors flag to suppress errors while typing
  const [showErrors, setShowErrors] = useState(false);

  const toggleExpand = () => {
    setIsExpanded(!isExpanded);
  };

  const copyToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(liveCode);
      setShowToast(true);
      setTimeout(() => setShowToast(false), 3000);
    } catch (err) {
      console.error("Failed to copy: ", err);
    }
  };

  // Debounce liveCode update to debouncedCode by 500ms
  useEffect(() => {
    setShowErrors(false); // hide errors while typing
    const handler = setTimeout(() => {
      setDebouncedCode(liveCode);
      setShowErrors(true); // show errors after debounce
    }, 500);

    return () => clearTimeout(handler);
  }, [liveCode]);

  const customTheme = {
    plain: {
      color: "var(--gray-100)",
      backgroundColor: "var(--black)",
    },
    styles: [
      {
        types: [
          "prolog",
          "comment",
          "block-comment",
          "doctype",
          "cdata",
          "class-name",
        ],
        style: {
          color: "var(--brand-1)",
        },
      },
      {
        types: ["attr-name"],
        style: {
          color: "var(--brand-2)",
        },
      },
      {
        types: ["punctuation"],
        style: {
          color: "var(--gray-100)",
        },
      },
    ],
  };

  return (
    <>
      {showToast && (
        <Toast
          showToast={showToast}
          setShowToast={setShowToast}
          text="Copied to clipboard 🚀"
          time={3000}
          simple
        />
      )}
      <LiveProvider code={debouncedCode} scope={scope} theme={customTheme}>
        <Flex
          className={`ComponentPreviewCode ${
            vertical ? "verticalLayout" : ""
          } ${isExpanded ? "Expanded" : ""}`}
        >
          <LivePreview />
          <ScrollArea style={{ height: "100%" }}>
            {/* Controlled LiveEditor */}
            <LiveEditor onChange={setLiveCode} value={liveCode} />
          </ScrollArea>
          <Paragraph className="Error">{showErrors && <LiveError />}</Paragraph>
          <Flex className="actions" gap={100}>
            <IconButton size="medium" variant="ghost" onClick={copyToClipboard}>
              <CopyIcon />
            </IconButton>
            <IconButton variant="solid" size="medium" onClick={toggleExpand}>
              <SizeIcon />
            </IconButton>
          </Flex>
        </Flex>
      </LiveProvider>
    </>
  );
};

export default ReactLive;
